import { useState, useEffect, useCallback } from "react";
import { ref, listAll, getDownloadURL, getMetadata } from "firebase/storage";
import { getFirebaseStorage } from "@/lib/firebase";

export interface ProjectImage {
  /** File name (e.g., "photo.jpg") */
  name: string;
  /** Signed download URL */
  downloadUrl: string;
  /** Photo capture date extracted from EXIF (optional) */
  photoDate?: Date;
}

// Files to exclude from project images (e.g., system files, logos)
const EXCLUDED_FILES = ["logo_gemeente_utrecht.png"];

/**
 * Hook to fetch all images from a project's storage folder
 */
export function useProjectImages(userId: string, projectId: string) {
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchImages = useCallback(async () => {
    if (!userId || !projectId) {
      setImages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const storage = getFirebaseStorage();
      const imagesPath = `users/${userId}/projects/${projectId}/images`;
      const folderRef = ref(storage, imagesPath);

      const result = await listAll(folderRef);

      // Filter out excluded files and fetch download URLs in parallel
      const filteredItems = result.items.filter(
        (itemRef) => !EXCLUDED_FILES.includes(itemRef.name.toLowerCase())
      );

      const imagePromises = filteredItems.map(async (itemRef) => {
        // Fetch download URL and metadata in parallel
        const [downloadUrl, metadata] = await Promise.all([
          getDownloadURL(itemRef),
          getMetadata(itemRef),
        ]);

        // Parse photo date from custom metadata if available
        let photoDate: Date | undefined;
        const photoDateStr = metadata.customMetadata?.photoDate;
        if (photoDateStr) {
          const parsed = new Date(photoDateStr);
          if (!isNaN(parsed.getTime())) {
            photoDate = parsed;
          }
        }

        return {
          name: itemRef.name,
          downloadUrl,
          photoDate,
        } satisfies ProjectImage;
      });

      const projectImages = await Promise.all(imagePromises);
      setImages(projectImages);
    } catch (err) {
      // Folder may not exist yet - this is expected for new projects
      console.debug("No images found:", err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return {
    images,
    loading,
    error,
    refetch: fetchImages,
  };
}
